import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { 
  User, Building, MapPin, Globe, Phone, Mail, DollarSign, 
  Clock, Star, Calendar, Award, Eye, UserPlus, Check 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicProfile = ({ userId, isModal = false, onClose = null }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [addingToNetwork, setAddingToNetwork] = useState(false);
  const [addedToNetwork, setAddedToNetwork] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchProfile();
    }
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile/${userId}`);
      if (response.data.status === 'success') {
        setProfile(response.data.profile);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = (type, contact) => {
    if (type === 'phone' && contact) {
      window.location.href = `tel:${contact}`;
    } else if (type === 'email' && contact) {
      window.location.href = `mailto:${contact}`;
    } else if (type === 'website' && contact) {
      window.open(contact.startsWith('http') ? contact : `https://${contact}`, '_blank');
    }
  };

  const handleAddToNetwork = async () => {
    if (!user || !profile) return;
    
    setAddingToNetwork(true);
    setAlertMessage(null);
    
    try {
      // Create friend record from provider profile
      const friendData = {
        name: profile.name,
        email: profile.contact_email || '',
        phone: profile.contact_phone || '',
        company: profile.business_name || '',
        services: profile.services_provided || [],
        user_id: user.id
      };

      const response = await axios.post(`${API_URL}/api/friends`, friendData);
      
      if (response.data.status === 'success') {
        setAddedToNetwork(true);
        setAlertMessage({
          type: 'success',
          message: `${profile.name} has been added to your network!`
        });
        
        // Store success message for parent component
        if (isModal) {
          sessionStorage.setItem('network_alert', `${profile.name} has been successfully added to your network!`);
        }
        
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setAlertMessage(null);
          if (isModal && onClose) {
            onClose(); // Close modal after success
          }
        }, 2000);
      } else if (response.data.status === 'error') {
        // Handle duplicate friend case
        setAddedToNetwork(true);
        setAlertMessage({
          type: 'error',
          message: response.data.message
        });
        
        setTimeout(() => {
          setAlertMessage(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error adding to network:', error);
      let errorMessage = 'Failed to add to network. Please try again.';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
        if (errorMessage.includes('already in your network')) {
          setAddedToNetwork(true);
        }
      }
      
      setAlertMessage({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setAddingToNetwork(false);
    }
  };

  const getServiceColor = (service) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800',
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800'
    ];
    const index = service.length % colors.length;
    return colors[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: 'var(--brand-navy)'}}></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center p-8">
        <User className="w-12 h-12 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">Profile not found</p>
      </div>
    );
  }

  const profileContent = (
    <div className="space-y-6">
      {/* Alert Messages */}
      {alertMessage && (
        <Alert className={`${
          alertMessage.type === 'success' 
            ? 'border-green-200 bg-green-50' 
            : 'border-red-200 bg-red-50'
        }`}>
          <AlertDescription className={`${
            alertMessage.type === 'success' 
              ? 'text-green-700' 
              : 'text-red-700'
          }`}>
            {alertMessage.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Profile Picture */}
        <div className="flex-shrink-0">
          {profile.profile_picture ? (
            <img
              src={profile.profile_picture.startsWith('data:') ? profile.profile_picture : `${API_URL}${profile.profile_picture}`}
              alt={profile.name}
              className="w-32 h-32 rounded-full object-cover border-4"
              style={{borderColor: 'var(--brand-navy)'}}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className={`w-32 h-32 rounded-full flex items-center justify-center text-white text-4xl font-semibold ${profile.profile_picture ? 'hidden' : 'flex'}`}
            style={{background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'}}
          >
            {profile.name.charAt(0).toUpperCase()}
          </div>
        </div>

        {/* Basic Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{color: 'var(--brand-navy)'}}>
              {profile.name}
            </h1>
            {profile.availability && (
              <Badge 
                variant="outline"
                className={`${
                  profile.availability === 'Available' ? 'bg-green-100 text-green-800' :
                  profile.availability === 'Busy' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}
              >
                {profile.availability}
              </Badge>
            )}
          </div>

          {profile.business_name && (
            <div className="flex items-center gap-2 mb-3">
              <Building className="w-4 h-4 text-gray-500" />
              <span className="text-lg font-medium" style={{color: 'var(--brand-navy)'}}>{profile.business_name}</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
            {profile.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{profile.location}</span>
              </div>
            )}
            
            {profile.years_experience && (
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" />
                <span>{profile.years_experience} years experience</span>
              </div>
            )}
            
            {profile.hourly_rate && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                <span>{profile.hourly_rate}</span>
              </div>
            )}
            
            {profile.created_at && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Member since {new Date(profile.created_at).getFullYear()}</span>
              </div>
            )}
          </div>

          {profile.bio && (
            <p className="mt-4 text-gray-700 leading-relaxed">
              {profile.bio}
            </p>
          )}
        </div>
      </div>

      {/* Services */}
      {profile.services_provided && profile.services_provided.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-3" style={{color: 'var(--brand-navy)'}}>
            Services Provided
          </h2>
          <div className="flex flex-wrap gap-2">
            {profile.services_provided.map((service, index) => (
              <Badge
                key={index}
                variant="secondary"
                className={`${getServiceColor(service)} text-sm px-3 py-1`}
              >
                {service}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Portfolio */}
      {profile.portfolio_images && profile.portfolio_images.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-3" style={{color: 'var(--brand-navy)'}}>
            Work Portfolio ({profile.portfolio_images?.length || 0} images)
          </h2>
          {console.log('🖼️ Portfolio images in PublicProfile:', profile.portfolio_images)}
          {console.log('🖼️ Portfolio images type:', typeof profile.portfolio_images)}
          {console.log('🖼️ Portfolio images array:', Array.isArray(profile.portfolio_images))}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {profile.portfolio_images?.map((imageUrl, index) => {
              console.log(`🖼️ RAW Image ${index}:`, imageUrl?.substring(0, 50));
              
              // DIFFERENT APPROACH: Handle base64 images with explicit rendering
              let imageSrc;
              let isBase64 = false;
              
              if (imageUrl && imageUrl.startsWith('data:image')) {
                imageSrc = imageUrl;
                isBase64 = true;
                console.log(`🖼️ Image ${index}: Using base64 (length: ${imageUrl.length})`);
              } else {
                imageSrc = `${API_URL}${imageUrl}`;
                console.log(`🖼️ Image ${index}: Using file URL: ${imageSrc}`);
              }
              
              return (
                <div
                  key={index}
                  className="cursor-pointer group relative"
                  onClick={() => setSelectedImage(imageUrl)}
                  style={{minHeight: '128px', backgroundColor: '#f3f4f6'}}
                >
                  {isBase64 ? (
                    <img
                      src={imageSrc}
                      alt={`Portfolio ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border group-hover:opacity-80 transition-opacity"
                      style={{maxWidth: '100%', height: '128px'}}
                      onLoad={() => console.log(`✅ Base64 image ${index} loaded`)}
                      onError={() => {
                        console.log(`❌ Base64 image ${index} failed`);
                      }}
                    />
                  ) : (
                    <img
                      src={imageSrc}
                      alt={`Portfolio ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border group-hover:opacity-80 transition-opacity"
                      onLoad={() => console.log(`✅ File image ${index} loaded`)}
                      onError={(e) => {
                        console.log(`❌ File image ${index} failed`);
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="absolute bottom-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                    {isBase64 ? 'B64' : 'FILE'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Contact Information */}
      <div>
        <h2 className="text-xl font-semibold mb-3" style={{color: 'var(--brand-navy)'}}>
          Get in Touch
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {profile.contact_phone && (
            <Button
              variant="outline"
              onClick={() => handleContact('phone', profile.contact_phone)}
              className="flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" />
              Call
            </Button>
          )}
          
          {profile.contact_email && (
            <Button
              variant="outline"
              onClick={() => handleContact('email', profile.contact_email)}
              className="flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Email
            </Button>
          )}
          
          {profile.website && (
            <Button
              variant="outline"
              onClick={() => handleContact('website', profile.website)}
              className="flex items-center justify-center gap-2"
            >
              <Globe className="w-4 h-4" />
              Website
            </Button>
          )}
          
          <Button
            className="flex items-center justify-center gap-2 text-white"
            style={{
              background: addedToNetwork 
                ? 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
                : 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-dark) 100%)'
            }}
            onClick={handleAddToNetwork}
            disabled={addingToNetwork || addedToNetwork}
          >
            {addedToNetwork ? (
              <>
                <Check className="w-4 h-4" />
                In Your Network
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                {addingToNetwork ? 'Adding...' : 'Add to Network'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Portfolio Image</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center">
              <img
                src={selectedImage.startsWith('data:') ? selectedImage : `${API_URL}${selectedImage}`}
                alt="Portfolio"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  if (isModal) {
    return (
      <Dialog open={isModal} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" style={{color: 'var(--brand-navy)'}} />
              Professional Profile
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {profileContent}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardContent className="p-6">
        {profileContent}
      </CardContent>
    </Card>
  );
};

export default PublicProfile;